import { Box, Card, Grid } from '@mui/material';
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { EICSRadioData, RoutingProps } from './IntakeClientsInfo';
import MediumTypography from '../../components/formlib/MediumTypography';
import CustomRadioButton from '../../components/formlib/CustomRadioButton';
import { Formik, FormikProps } from 'formik';
import * as yup from 'yup';
import CommentTextbox from '../../components/formlib/CommentTextbox';
import Textinput from '../../components/formlib/Textinput';
import ButtonComponent from '../../components/formlib/ButtonComponent';
import { LoaderContext, LoaderContextType } from '../../layouts/AppSidebar';
import {
  getIntakePregnancyInfo,
  putIntakePregnancyInfo,
} from '../../services/configApi/forms/Intake/IntakeServices';
import { IntakePregnancyInfoType, ResponseType } from '../../utils/type';
import { ApiError, isCustomError } from '../../services/ApiResponseHandler';
import ModalPopup from '../../components/formlib/ModalPopup';
import { showCancelToaster } from './IntakeTabNavigationComponent';
import { UnSavedChangesContext } from '../../context/UnSavedChangesProvider';
import _, { debounce } from 'lodash';
import CheckBoxComponent from '../../components/formlib/CheckBoxComponent';
import SnackBarComponent from '../../components/formlib/SnackBarComponent';
import { useMultiplePermissions } from '../../hooks/usePermissions';
import { REFERRAL_STATUS_KEY } from '../../services/Constant';

export const IntakeRadioData = [
  {
    value: 'Yes',
    labelId: 'Intake.RadioLabelA',
    defaultLabel: 'Yes',
  },
  {
    value: 'No',
    labelId: 'Intake.RadioLabelB',
    defaultLabel: 'No',
  },
];

export const BirthRadioData = [
  {
    value: 'In Pounds',
    labelId: 'Intake.BirthRadioLabelA',
    defaultLabel: 'In Pounds',
  },
  {
    value: 'In Grams',
    labelId: 'Intake.BirthRadioLabelB',
    defaultLabel: 'In Grams',
  },
  {
    value: 'Unknown',
    labelId: 'Intake.RadioLabelC',
    defaultLabel: 'Unknown',
  },
];

const IntakePregnancyInitialValues: IntakePregnancyInfoType = {
  prenatal: {
    prenatalCare: '',
    prenatalCareStart: '',
    pregnancyComplication: '',
    pregnancyComplicationDesc: '',
    prenatalTest: '',
    prenatalTestDesc: '',
  },
  drugUse: {
    alcoholUse: '',
    alcoholUseDesc: '',
    tobaccoUse: '',
    tobaccoUseDesc: '',
    recreationalDrugUse: '',
    recreationalDrugUseDesc: '',
    prescriptionDrugUse: '',
    prescriptionDrugUseDesc: '',
  },
  delivery: {
    laborComplicationDesc: '',
    gestationalParentMedication: '',
    gestationalParentMedicationDesc: '',
  },
  tests: {
    specialMedicalEquipment: '',
    specialMedicalEquipmentDesc: '',
    parentStayInHospital: '',
    babyStayInHospital: '',
    newbornHearingScreening: '',
    parentHadTimes: '',
    parentHadTimesDesc: '',
    parentPhysicalFeel: '',
    parentPhysicalFeelDesc: '',
  },
  eics: {
    multipleBirth: '',
    specialCareAfterBirth: '',
    specialCareAfterBirthDesc: '',
    birthMotherParity: '',
    diagnosisOfSubstanceExposedNewborn: '',
    gestationalAge: '',
    birthWeight: '',
    weightInLbs: '',
    weightInOz: '',
    weightInGrams: '',
    childDiagnosedAtBirth: '',
    siblingWithSEN: '',
    childInNICU: '',
    daysInNICU: '',
  },
  createdOn: '',
  modifiedOn: '',
  createdBy: '',
  modifiedBy: '',
  isCurrentlyHeld: false,
  heldBy: '',
  isSigned: false,
  reviewdBy: '',
  reviewdDate: '',
  comment: '',
};

const validationSchema = yup.object().shape({
  prenatalCare: yup.string().nullable(),
  prenatalCareStart: yup.string().nullable(),
  prenatalCareRecievedAt: yup.string().nullable(),
  pregnancyProblems: yup.string().nullable(),
  pregnancyProblemOther: yup.string().nullable(),
  prenatalTests: yup.string().nullable(),
  problemWhenChildBorn: yup.string().nullable(),
  problemWhenChildBornDeal: yup.string().nullable(),
  problemWhenChildBornNow: yup.string().nullable(),
  alcoholUse: yup.string().nullable(),
  tobaccoUse: yup.string().nullable(),
  prescriptionDrugUse: yup.string().nullable(),
  recreationalDrugUse: yup.string().nullable(),
  medicationDuringTrimester: yup.string().nullable(),
  complicationWithPregnancy: yup.string().nullable(),
  babyBornOnTime: yup.string().nullable(),
  babyBornEarlyOrLate: yup.string().nullable(),
  gestationalAgeCheck: yup.boolean().nullable(),
  gestationalAge: yup.string().nullable(),
  labor: yup.string().nullable(),
  caesareanExplain: yup.string().nullable(),
  considerationsAboutLaborOther: yup.string().nullable(),
  diagnosisOfSubstanceExposedNewborn: yup.string().nullable(),
  medicationsAnesthesia: yup.string().nullable(),
  birthWeight: yup.string().nullable(),
  weightInLbs: yup.string().when('birthWeight', {
    is: 'In Pounds',
    then: yup.string().required('Required').nullable(),
    otherwise: yup.string().nullable(),
  }),
  weightInOz: yup.string().when('birthWeight', {
    is: 'In Pounds',
    then: yup.string().required('Required').nullable(),
    otherwise: yup.string().nullable(),
  }),
  weightInGrams: yup.string().when('birthWeight', {
    is: 'In Grams',
    then: yup.string().required('Required').nullable(),
    otherwise: yup.string().nullable(),
  }),
  multipleBirth: yup.string().nullable(),
  neonatalCourse: yup.string().nullable(),
  specialCareAfterBirth: yup.string().nullable(),
  hospitalNameNICU: yup.string().nullable(),
  hospitalPhoneNoNICU: yup.string().nullable(),
  howLongNICU: yup.string().nullable(),
  noOfDaysCCN: yup.string().nullable(),
  hospitalNameSC: yup.string().nullable(),
  hospitalPhoneNoSC: yup.string().nullable(),
  howLongSC: yup.string().nullable(),
  hospitalNameRN: yup.string().nullable(),
  hospitalPhoneNoRN: yup.string().nullable(),
  howLongRN: yup.string().nullable(),
  roomedInRN: yup.string().nullable(),
  motherInHospital: yup.string().nullable(),
  dischargeDate: yup.date().nullable().nullable(),
  treatmentOther: yup.string().nullable(),
  newbornHearingScreening: yup.string().nullable(),
  specialNeedsMedicalEquipment: yup.string().nullable(),
  hearingEvaluation: yup.string().nullable(),
  hearingEvaluationDesc: yup.string().nullable(),
  visionScreening: yup.string().nullable(),
  visionScreeningDesc: yup.string().nullable(),
  hearingVisionConcerns: yup.string().nullable(),
  childInNICU: yup.string().nullable(),
  daysInNICU: yup.string().when('childInNICU', {
    is: 'Yes',
    then: yup.string().required('Required'),
    otherwise: yup.string().nullable(),
  }),
  daysInNICUCheck: yup.boolean().nullable(),
  comment: yup.string(),
});

interface UnsavedChangesWatcherProps {
  hasUnsavedChanges: boolean;
  onUnsavedChangesChange: (hasUnsavedChanges: boolean) => void;
}

const UnsavedChangesWatcher = ({
  hasUnsavedChanges,
  onUnsavedChangesChange,
}: UnsavedChangesWatcherProps) => {
  useLayoutEffect(() => {
    onUnsavedChangesChange(hasUnsavedChanges);
  }, [hasUnsavedChanges, onUnsavedChangesChange]);

  return null;
};

const IntakePregnancyInfo: React.FC<RoutingProps> = ({ setTabProp }) => {
  const clientId = localStorage.getItem('ClientId');
  const [intakePregnancyInfo, setIntakePregnancyInfo] =
    useState<IntakePregnancyInfoType>(IntakePregnancyInitialValues);

  const { toggleLoader } = React.useContext(LoaderContext) as LoaderContextType;

  const [toastrDefaultMessage, setToastrDefaultMessage] = React.useState('');
  const [toastrId, setToastrId] = useState<string>();
  const [successOrError, setSuccessOrError] = useState<ResponseType>('success');
  const [openModal, setOpenModal] = useState(false);
  const [gestationalAgeCheck, setGestationalAgeCheck] =
    useState<boolean>(false);
  const [daysInNICUCheck, setDaysInNICUCheck] = useState<boolean>(false);
  const parentRef = React.useRef<HTMLDivElement>(null);
  const { handleUpdateUnsavedChanges } = useContext(UnSavedChangesContext);
  const permissions = useMultiplePermissions([
    'backend.clients',
    'backend.in_active_referrals',
  ]);
  const referralStatus = localStorage.getItem(REFERRAL_STATUS_KEY) ?? 'None';
  const [hasEditPermission, setHasEditPermission] = useState<boolean>(
    referralStatus === 'Inactive'
      ? permissions['backend.clients'].canEdit &&
          permissions['backend.in_active_referrals'].canEdit
      : permissions['backend.clients'].canEdit ||
          permissions['backend.in_active_referrals'].canEdit,
  );

  useEffect(() => {
    if (!hasEditPermission) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Tab') {
          e.preventDefault();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [hasEditPermission]);

  useEffect(() => {
    const handleIntakeGet = async () => {
      handleIntakeGetAPI();
    };
    handleIntakeGet();
  }, []);

  const handleIntakeGetAPI = async () => {
    window.scrollTo(0, 0);
    if (clientId) {
      toggleLoader(true);
      getIntakePregnancyInfo(clientId)
        .then((response: IntakePregnancyInfoType) => {
          if (response.isSigned) {
            setHasEditPermission(false);
          } else if (response.isCurrentlyHeld) {
            setHasEditPermission(false);
            showCancelToaster(response.heldBy);
          }
          setGestationalAgeCheck(response.eics.gestationalAge === 'Unknown');
          setDaysInNICUCheck(response.eics.daysInNICU === 'Unknown');
          setIntakePregnancyInfo({
            prenatal: {
              pregnancyComplication: response.prenatal.pregnancyComplication,
              pregnancyComplicationDesc:
                response.prenatal.pregnancyComplicationDesc,
              prenatalCare: response.prenatal.prenatalCare,
              prenatalCareStart: response.prenatal.prenatalCareStart,
              prenatalTest: response.prenatal.prenatalTest,
              prenatalTestDesc: response.prenatal.prenatalTestDesc,
            },
            delivery: {
              gestationalParentMedication:
                response.delivery.gestationalParentMedication,
              gestationalParentMedicationDesc:
                response.delivery.gestationalParentMedicationDesc,
              laborComplicationDesc: response.delivery.laborComplicationDesc,
            },
            tests: {
              babyStayInHospital: response.tests.babyStayInHospital,
              newbornHearingScreening: response.tests.newbornHearingScreening,
              parentHadTimes: response.tests.parentHadTimes,
              parentHadTimesDesc: response.tests.parentHadTimesDesc,
              parentPhysicalFeel: response.tests.parentPhysicalFeel,
              parentPhysicalFeelDesc: response.tests.parentPhysicalFeelDesc,
              parentStayInHospital: response.tests.parentStayInHospital,
              specialMedicalEquipment: response.tests.specialMedicalEquipment,
              specialMedicalEquipmentDesc:
                response.tests.specialMedicalEquipmentDesc,
            },
            drugUse: {
              alcoholUse: response.drugUse.alcoholUse,
              alcoholUseDesc: response.drugUse.alcoholUseDesc,
              prescriptionDrugUse: response.drugUse.prescriptionDrugUse,
              prescriptionDrugUseDesc: response.drugUse.prescriptionDrugUseDesc,
              recreationalDrugUse: response.drugUse.recreationalDrugUse,
              recreationalDrugUseDesc: response.drugUse.recreationalDrugUseDesc,
              tobaccoUse: response.drugUse.tobaccoUse,
              tobaccoUseDesc: response.drugUse.tobaccoUseDesc,
            },
            eics: {
              multipleBirth: response.eics.multipleBirth,
              specialCareAfterBirth: response.eics.specialCareAfterBirth,
              specialCareAfterBirthDesc:
                response.eics.specialCareAfterBirthDesc,
              birthMotherParity: response.eics.birthMotherParity,
              birthWeight: response.eics.birthWeight,
              childDiagnosedAtBirth: response.eics.childDiagnosedAtBirth,
              childInNICU: response.eics.childInNICU,
              daysInNICU: response.eics.daysInNICU,
              diagnosisOfSubstanceExposedNewborn:
                response.eics.diagnosisOfSubstanceExposedNewborn,
              gestationalAge: response.eics.gestationalAge,
              siblingWithSEN: response.eics.siblingWithSEN,
              weightInGrams: response.eics.weightInGrams.trim(),
              weightInLbs: response.eics.weightInLbs.trim(),
              weightInOz: response.eics.weightInOz.trim(),
            },
            createdBy: response.createdBy,
            createdOn: response.createdOn,
            heldBy: response.heldBy,
            isCurrentlyHeld: response.isCurrentlyHeld,
            isSigned: response.isSigned,
            modifiedBy: response.modifiedBy,
            modifiedOn: response.modifiedOn,
            reviewdBy: response.reviewdBy,
            reviewdDate: response.reviewdDate,
            comment: response.comment ? response.comment : '',
          });

          setTimeout(() => {
            toggleLoader(false);
          }, 100);
        })
        .catch((error) => {
          toggleLoader(false);
          setSuccessOrError('error');
          if (isCustomError(error)) {
            const apiError = error as ApiError;
            setToastrId(apiError.id);
            setToastrDefaultMessage(apiError.message);
          } else {
            setToastrId(error);
            setToastrDefaultMessage(error);
          }
        });
    }
  };

  const formikRef = useRef<FormikProps<IntakePregnancyInfoType>>(null);

  const handleFormSubmit = useCallback(
    debounce((formValue: IntakePregnancyInfoType) => {
      updateWithDebounce(formValue);
    }, 500),
    [],
  );

  const updateWithDebounce = async (values: IntakePregnancyInfoType) => {
    const params: IntakePregnancyInfoType = {
      prenatal: {
        prenatalCare: values.prenatal.prenatalCare,
        prenatalCareStart: values.prenatal.prenatalCareStart,
        pregnancyComplication: values.prenatal.pregnancyComplication,
        pregnancyComplicationDesc: values.prenatal.pregnancyComplicationDesc,
        prenatalTest: values.prenatal.prenatalTest,
        prenatalTestDesc: values.prenatal.prenatalTestDesc,
      },
      drugUse: {
        alcoholUse: values.drugUse.alcoholUse,
        alcoholUseDesc: values.drugUse.alcoholUseDesc,
        tobaccoUse: values.drugUse.tobaccoUse,
        tobaccoUseDesc: values.drugUse.tobaccoUseDesc,
        recreationalDrugUse: values.drugUse.recreationalDrugUse,
        recreationalDrugUseDesc: values.drugUse.recreationalDrugUseDesc,
        prescriptionDrugUse: values.drugUse.prescriptionDrugUse,
        prescriptionDrugUseDesc: values.drugUse.prescriptionDrugUseDesc,
      },
      delivery: {
        laborComplicationDesc: values.delivery.laborComplicationDesc,
        gestationalParentMedication:
          values.delivery.gestationalParentMedication,
        gestationalParentMedicationDesc:
          values.delivery.gestationalParentMedicationDesc,
      },
      tests: {
        specialMedicalEquipment: values.tests.specialMedicalEquipment,
        specialMedicalEquipmentDesc: values.tests.specialMedicalEquipmentDesc,
        parentStayInHospital: values.tests.parentStayInHospital,
        babyStayInHospital: values.tests.babyStayInHospital,
        newbornHearingScreening: values.tests.newbornHearingScreening,
        parentHadTimes: values.tests.parentHadTimes,
        parentHadTimesDesc: values.tests.parentHadTimesDesc,
        parentPhysicalFeel: values.tests.parentPhysicalFeel,
        parentPhysicalFeelDesc: values.tests.parentPhysicalFeelDesc,
      },
      eics: {
        multipleBirth: values.eics.multipleBirth,
        specialCareAfterBirth: values.eics.specialCareAfterBirth,
        specialCareAfterBirthDesc: values.eics.specialCareAfterBirthDesc,
        birthMotherParity: values.eics.birthMotherParity,
        diagnosisOfSubstanceExposedNewborn:
          values.eics.diagnosisOfSubstanceExposedNewborn,
        gestationalAge: values.eics.gestationalAge,
        birthWeight: values.eics.birthWeight,
        weightInLbs: values.eics.weightInLbs.trim(),
        weightInOz: isNaN(parseFloat(values.eics.weightInOz))
          ? ''
          : parseFloat(values.eics.weightInOz).toFixed(2).trim(),
        weightInGrams: values.eics.weightInGrams.trim(),
        childDiagnosedAtBirth: values.eics.childDiagnosedAtBirth,
        siblingWithSEN: values.eics.siblingWithSEN,
        childInNICU: values.eics.childInNICU,
        daysInNICU: values.eics.daysInNICU,
      },
      createdOn: values.createdOn,
      modifiedOn: values.modifiedOn,
      createdBy: values.createdBy,
      modifiedBy: values.modifiedBy,
      isCurrentlyHeld: values.isCurrentlyHeld,
      heldBy: values.heldBy,
      isSigned: values.isSigned,
      reviewdBy: values.reviewdBy,
      reviewdDate: values.reviewdDate,
      comment: values.comment,
    };

    toggleLoader(true);
    if (clientId) {
      putIntakePregnancyInfo(clientId, params)
        .then((response) => {
          if (response) {
            setSuccessOrError('success');
            setToastrId('Intake.successMsg');
            setToastrDefaultMessage('Intake updated successfully');

            setTimeout(() => {
              toggleLoader(false);
              setTabProp((value) => {
                sessionStorage.setItem(
                  'intakeTabName',
                  String(value.tabPosition),
                );
                return {
                  tabPosition: value.tabPosition,
                };
              });
            }, 1000);

            handleIntakeGetAPI();
          }
        })
        .catch((error) => {
          toggleLoader(false);
          setSuccessOrError('error');
          if (isCustomError(error)) {
            const apiError = error as ApiError;
            setToastrId(apiError.id);
            setToastrDefaultMessage(apiError.message);
          } else {
            setToastrId(error);
            setToastrDefaultMessage(error);
          }
        });
    }
  };

  const handleCancellationFlow = () => {
    if (_.isEqual(intakePregnancyInfo, formikRef.current?.values)) {
      return false;
    } else {
      return true;
    }
  };

  return (
    <>
      {toastrId && (
        <SnackBarComponent
          open={toastrId !== undefined}
          handleClose={() => {
            setToastrId(undefined);
          }}
          labelId={toastrId}
          defaultMessageId={toastrDefaultMessage}
          successOrError={successOrError}
          snackbarProps={{
            top: '60px !important',
          }}
        />
      )}
      <Box
        ref={parentRef}
        style={{
          pointerEvents: hasEditPermission ? 'auto' : 'none',
          opacity: hasEditPermission ? 'unset' : '0.8',
        }}
      >
        <Card className="formCardview gray3 borderRadius4 mb-md p-sm mt-md">
          <Formik
            initialValues={intakePregnancyInfo}
            innerRef={formikRef}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={(values) => {
              handleFormSubmit(values);
            }}
          >
            {({ values, setFieldValue }) => {
              return (
                <Box component="div">
                  <UnsavedChangesWatcher
                    hasUnsavedChanges={handleCancellationFlow()}
                    onUnsavedChangesChange={handleUpdateUnsavedChanges}
                  />
                  <Box
                    component="div"
                    className="p-sm gray3 borderRadius4"
                    sx={{ border: '1px solid #00C6B8' }}
                  >
                    <MediumTypography
                      labelid="Intake.pregnancyBeforeTheBirth"
                      defaultlabel="Pregnancy Before the Birth"
                      fontSize="16px"
                      fontweight={600}
                      textColor="#2A4241"
                    />
                    <Box className="align__items__center mt-md">
                      <Box className="flex__">
                        <MediumTypography
                          labelid="IntakeFamilyHistory.birthMotherParity"
                          defaultlabel="Mother less than 17 years of age and/or with 3 or more children before 20 (Birth Mother Parity) ?"
                        />
                        <MediumTypography
                          labelid="Intake.Eics"
                          defaultlabel="(EICS)"
                          sxProps={{ fontStyle: 'italic' }}
                          className="ml-xs"
                        />
                      </Box>
                      <CustomRadioButton
                        data={EICSRadioData}
                        value={values.eics.birthMotherParity}
                        onSelectItem={(value) => {
                          if (value) {
                            setFieldValue('eics.birthMotherParity', value);
                          }
                        }}
                      />
                    </Box>
                    <Box className="align__items__center mt-md">
                      <MediumTypography
                        labelid="IntakePregnancy.duringThisPregnancy"
                        defaultlabel="During this pregnancy, was prenatal care received?"
                        className="mr-lg"
                      />
                      <CustomRadioButton
                        data={EICSRadioData}
                        value={values.prenatal.prenatalCare}
                        onSelectItem={(value) => {
                          if (value) {
                            setFieldValue('prenatal.prenatalCare', value);
                          }
                        }}
                      />
                    </Box>
                    <Box className="mt-md">
                      <MediumTypography
                        labelid="IntakePregnancy.prenatalCareStartAndWhere"
                        defaultlabel="When did prenatal care start? Where was prenatal care provided?"
                        className="mr-lg"
                      />
                      <Grid
                        container
                        direction={'row'}
                        columnSpacing={'40px'}
                        className="mt-md mb-lg"
                      >
                        {' '}
                        <Grid item xs={9} lg={9}>
                          <CommentTextbox
                            Value={values.prenatal.prenatalCareStart}
                            placeholder="Comments"
                            name="prenatalCareStart"
                            maxLength={2000}
                            handlechange={(e) => {
                              setFieldValue('prenatal.prenatalCareStart', e);
                            }}
                            placeholderBackgroundColor="var(--thom-mono-gray3-bg)"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                    <Box className="mt-md">
                      <Box className="align__items__center">
                        <MediumTypography
                          labelid="IntakePregnancy.complicationsDuringThePregnancy"
                          defaultlabel="Were there any complications during the pregnancy?"
                          className="mr-lg"
                        />
                        <CustomRadioButton
                          data={EICSRadioData}
                          value={values.prenatal.pregnancyComplication}
                          onSelectItem={(value) => {
                            if (value) {
                              setFieldValue(
                                'prenatal.pregnancyComplication',
                                value,
                              );
                              setFieldValue(
                                'prenatal.pregnancyComplicationDesc',
                                '',
                              );
                            }
                          }}
                        />
                      </Box>
                      {values.prenatal.pregnancyComplication === 'Yes' && (
                        <Grid
                          container
                          direction={'row'}
                          columnSpacing={'40px'}
                          className="mt-md mb-lg"
                        >
                          {' '}
                          <Grid item xs={9} lg={9}>
                            <CommentTextbox
                              Value={values.prenatal.pregnancyComplicationDesc}
                              placeholder="Comments"
                              name="prenatalCareStart"
                              maxLength={2000}
                              handlechange={(e) => {
                                setFieldValue(
                                  'prenatal.pregnancyComplicationDesc',
                                  e,
                                );
                              }}
                              placeholderBackgroundColor="var(--thom-mono-gray3-bg)"
                            />
                          </Grid>
                        </Grid>
                      )}
                    </Box>
                    <Box className="mt-md">
                      <Box className="align__items__center">
                        <MediumTypography
                          labelid="IntakePregnancy.additionalTestsPerformedDuringThePregnancyOutsideOfTheUsualPrenatalTests"
                          defaultlabel="Were any additional tests performed during the pregnancy outside of the usual prenatal tests?"
                          className="mr-lg"
                        />
                        <CustomRadioButton
                          data={EICSRadioData}
                          value={values.prenatal.prenatalTest}
                          onSelectItem={(value) => {
                            if (value) {
                              setFieldValue('prenatal.prenatalTest', value);
                              setFieldValue('prenatal.prenatalTestDesc', '');
                            }
                          }}
                        />
                      </Box>
                      {values.prenatal.prenatalTest === 'Yes' && (
                        <Grid
                          container
                          direction={'row'}
                          columnSpacing={'40px'}
                          className="mt-md mb-lg"
                        >
                          {' '}
                          <Grid item xs={9} lg={9}>
                            <CommentTextbox
                              Value={values.prenatal.prenatalTestDesc}
                              placeholder="Comments"
                              name="prenatalCareStart"
                              maxLength={2000}
                              handlechange={(e) => {
                                setFieldValue('prenatal.prenatalTestDesc', e);
                              }}
                              placeholderBackgroundColor="var(--thom-mono-gray3-bg)"
                            />
                          </Grid>
                        </Grid>
                      )}
                    </Box>
                    <Box className="mt-md">
                      <MediumTypography
                        labelid="IntakePregnancy.substancesQuestion"
                        defaultlabel="While you were pregnant, did you use any of the following substances?"
                      />
                      <Box className="mt-sm">
                        <MediumTypography
                          labelid="IntakePregnancy.alcohol"
                          defaultlabel="Alcohol"
                          className="mr-lg"
                        />
                        <CustomRadioButton
                          data={IntakeRadioData}
                          value={values.drugUse.alcoholUse}
                          onSelectItem={(value) => {
                            if (value) {
                              setFieldValue('drugUse.alcoholUse', value);
                              setFieldValue('drugUse.alcoholUseDesc', '');
                            }
                          }}
                        />
                        {values.drugUse.alcoholUse === 'Yes' && (
                          <Grid
                            container
                            direction={'row'}
                            columnSpacing={'40px'}
                            className="mt-md mb-lg"
                          >
                            {' '}
                            <Grid item xs={9} lg={9}>
                              <CommentTextbox
                                Value={values.drugUse.alcoholUseDesc}
                                placeholder="Comments"
                                name="prenatalCareStart"
                                maxLength={2000}
                                handlechange={(e) => {
                                  setFieldValue('drugUse.alcoholUseDesc', e);
                                }}
                                placeholderBackgroundColor="var(--thom-mono-gray3-bg)"
                              />
                            </Grid>
                          </Grid>
                        )}
                      </Box>
                      <Box className="mt-sm">
                        <MediumTypography
                          labelid="IntakePregnancy.tobacco"
                          defaultlabel="Tobacco"
                          className="mr-lg"
                        />
                        <CustomRadioButton
                          data={IntakeRadioData}
                          value={values.drugUse.tobaccoUse}
                          onSelectItem={(value) => {
                            if (value) {
                              setFieldValue('drugUse.tobaccoUse', value);
                              setFieldValue('drugUse.tobaccoUseDesc', '');
                            }
                          }}
                        />
                        {values.drugUse.tobaccoUse === 'Yes' && (
                          <Grid
                            container
                            direction={'row'}
                            columnSpacing={'40px'}
                            className="mt-md mb-lg"
                          >
                            {' '}
                            <Grid item xs={9} lg={9}>
                              <CommentTextbox
                                Value={values.drugUse.tobaccoUseDesc}
                                placeholder="Comments"
                                name="prenatalCareStart"
                                maxLength={2000}
                                handlechange={(e) => {
                                  setFieldValue('drugUse.tobaccoUseDesc', e);
                                }}
                                placeholderBackgroundColor="var(--thom-mono-gray3-bg)"
                              />
                            </Grid>
                          </Grid>
                        )}
                      </Box>
                      <Box className="mt-sm">
                        <MediumTypography
                          labelid="IntakePregnancy.prescriptionDrugs"
                          defaultlabel="Prescription drugs"
                          className="mr-lg"
                        />
                        <CustomRadioButton
                          data={IntakeRadioData}
                          value={values.drugUse.prescriptionDrugUse}
                          onSelectItem={(value) => {
                            if (value) {
                              if (value === 'No') {
                                setFieldValue(
                                  'drugUse.prescriptionDrugUse',
                                  '',
                                );
                              }
                              setFieldValue(
                                'drugUse.prescriptionDrugUse',
                                value,
                              );
                              setFieldValue(
                                'drugUse.prescriptionDrugUseDesc',
                                '',
                              );
                            }
                          }}
                        />
                        {values.drugUse.prescriptionDrugUse === 'Yes' && (
                          <Grid
                            container
                            direction={'row'}
                            columnSpacing={'40px'}
                            className="mt-md mb-lg"
                          >
                            {' '}
                            <Grid item xs={9} lg={9}>
                              <CommentTextbox
                                Value={values.drugUse.prescriptionDrugUseDesc}
                                placeholder="Comments"
                                name="prenatalCareStart"
                                maxLength={2000}
                                handlechange={(e) => {
                                  setFieldValue(
                                    'drugUse.prescriptionDrugUseDesc',
                                    e,
                                  );
                                }}
                                placeholderBackgroundColor="var(--thom-mono-gray3-bg)"
                              />
                            </Grid>
                          </Grid>
                        )}
                      </Box>
                      <Box className="mt-sm">
                        <MediumTypography
                          labelid="IntakePregnancy.recreationalNonPrescriptionDrugs"
                          defaultlabel="Recreational/Non-prescription drugs"
                          className="mr-lg"
                        />
                        <CustomRadioButton
                          data={IntakeRadioData}
                          value={values.drugUse.recreationalDrugUse}
                          onSelectItem={(value) => {
                            if (value) {
                              if (value === 'No') {
                                setFieldValue(
                                  'drugUse.recreationalDrugUse',
                                  '',
                                );
                              }
                              setFieldValue(
                                'drugUse.recreationalDrugUse',
                                value,
                              );
                              setFieldValue(
                                'drugUse.recreationalDrugUseDesc',
                                '',
                              );
                            }
                          }}
                        />
                        {values.drugUse.recreationalDrugUse === 'Yes' && (
                          <Grid
                            container
                            direction={'row'}
                            columnSpacing={'40px'}
                            className="mt-md mb-lg"
                          >
                            {' '}
                            <Grid item xs={9} lg={9}>
                              <CommentTextbox
                                Value={values.drugUse.recreationalDrugUseDesc}
                                placeholder="Comments"
                                name="prenatalCareStart"
                                maxLength={2000}
                                handlechange={(e) => {
                                  setFieldValue(
                                    'drugUse.recreationalDrugUseDesc',
                                    e,
                                  );
                                }}
                                placeholderBackgroundColor="var(--thom-mono-gray3-bg)"
                              />
                            </Grid>
                          </Grid>
                        )}
                      </Box>
                    </Box>
                    <MediumTypography
                      labelid="Intake.Pregnancy.laborDelivery"
                      defaultlabel="Labor and Delivery"
                      fontSize="16px"
                      fontweight={600}
                      textColor="#2A4241"
                      className="mt-sm"
                    />
                    <Box className="mt-md">
                      <Box className="flex__">
                        <MediumTypography
                          labelid="IntakePregnancy.pregnancyWeeks"
                          defaultlabel="How many weeks long was the pregnancy?"
                        />
                        <MediumTypography
                          labelid="Intake.Eics"
                          defaultlabel="(EICS)"
                          sxProps={{ fontStyle: 'italic' }}
                          className="ml-xs"
                        />
                      </Box>
                      <Grid
                        container
                        direction={'row'}
                        columnSpacing={'20px'}
                        className="mt-md mb-lg"
                      >
                        <Grid item xs={4.5} lg={4.5}>
                          <Textinput
                            labelid="IntakePregnancy.gestationalAge"
                            defaultlabelid="Gestational Age (in weeks)"
                            Value={values.eics.gestationalAge}
                            inputProps={{ maxLength: 2 }}
                            handlechange={(text: string) => {
                              setFieldValue(
                                'eics.gestationalAge',
                                text.replace(/\D/g, ''),
                              );
                            }}
                            sxProps={{
                              backgroundColor: 'white',
                            }}
                            placeholderOnfloatBackgroundColor="var(--thom-mono-gray3-bg)"
                            disabled={gestationalAgeCheck}
                          />
                        </Grid>

                        <Grid
                          item
                          xs={4.5}
                          lg={4.5}
                          sx={{ display: 'flex', alignItems: 'center' }}
                        >
                          <CheckBoxComponent
                            CheckHandleChange={(e) => {
                              if (e.target.checked) {
                                setFieldValue('eics.gestationalAge', 'Unknown');
                                setGestationalAgeCheck(true);
                              } else {
                                setGestationalAgeCheck(false);
                                setFieldValue('eics.gestationalAge', '');
                              }
                            }}
                            ischecked={gestationalAgeCheck}
                            labelid="Intake.RadioLabelC"
                            defaultlabelid="Unknown"
                          />
                        </Grid>
                      </Grid>
                      <Box className="mt-sm">
                        <Box className="flex__">
                          <MediumTypography
                            labelid="IntakePregnancy.BirthWeigh"
                            defaultlabel="How much did the child weigh at birth?"
                          />
                          <MediumTypography
                            labelid="Intake.Eics"
                            defaultlabel="(EICS)"
                            sxProps={{ fontStyle: 'italic' }}
                            className="ml-xs"
                          />
                        </Box>
                        <CustomRadioButton
                          data={BirthRadioData}
                          value={values.eics.birthWeight}
                          onSelectItem={(value) => {
                            if (value) {
                              if (value === 'In Pounds') {
                                setFieldValue('weightInGrams', '');
                              } else {
                                setFieldValue('weightInLbs', '');
                                setFieldValue('weightInOz', '');
                              }
                              setFieldValue('eics.birthWeight', value);
                            }
                          }}
                        />
                      </Box>
                      {values.eics.birthWeight === 'In Pounds' && (
                        <Box
                          component="div"
                          display="flex"
                          flexDirection="row"
                          className="mt-md"
                        >
                          <Box className="mr-md">
                            <Textinput
                              name="weightInLbs"
                              labelid="weightInLbs"
                              defaultlabelid="Lbs *"
                              Value={values.eics.weightInLbs}
                              inputProps={{ maxLength: 3 }}
                              handlechange={(text: string) => {
                                setFieldValue(
                                  'eics.weightInLbs',
                                  text.replace(/\D/g, ''),
                                );
                              }}
                              sxProps={{
                                backgroundColor: 'white',
                              }}
                              placeholderOnfloatBackgroundColor="var(--thom-mono-gray3-bg)"
                            />
                          </Box>

                          <Box>
                            <Textinput
                              name="weightInOz"
                              labelid="weightInOz"
                              defaultlabelid="Oz (0-15) *"
                              Value={values.eics.weightInOz}
                              inputProps={{ maxLength: 5 }}
                              handlechange={(text: string) => {
                                // Auto-prepend 0 if user starts with '.'
                                if (text.startsWith('.')) {
                                  text = '0' + text;
                                  setFieldValue(
                                    'eics.weightInOz',
                                    text.toString(),
                                  );
                                }

                                // Full match for up to 2 digits before and after dot
                                const validDecimal =
                                  /^\d{1,2}(\.\d{0,2})?$/.exec(text);

                                if (validDecimal) {
                                  const num = Number.parseFloat(text || '0');
                                  if (num >= 0 && num <= 15) {
                                    setFieldValue('eics.weightInOz', text);
                                  } else {
                                    setFieldValue('eics.weightInOz', '');
                                  }
                                } else {
                                  // Sanitize: remove non-digit/dot, limit digits & 1 dot
                                  const cleaned = text
                                    .replace(/[^\d.]/g, '') // remove invalid chars
                                    .replace(
                                      /^(\d{0,2})(\.\d{0,2})?.*$/,
                                      '$1$2',
                                    ); // limit format

                                  setFieldValue('eics.weightInOz', cleaned);
                                }
                              }}
                              onblur={() => {
                                if (values.eics.weightInOz) {
                                  setFieldValue(
                                    'eics.weightInOz',
                                    values.eics.weightInOz.startsWith('.')
                                      ? '0.00'
                                      : parseFloat(
                                          values.eics.weightInOz,
                                        ).toFixed(2),
                                  );
                                }
                              }}
                              sxProps={{
                                backgroundColor: 'white',
                              }}
                              placeholderOnfloatBackgroundColor="var(--thom-mono-gray3-bg)"
                            />
                          </Box>
                        </Box>
                      )}

                      {values.eics.birthWeight === 'In Grams' && (
                        <Box
                          component="div"
                          display="flex"
                          flexDirection="row"
                          className="mt-md"
                        >
                          <Box>
                            <Textinput
                              name="weightInGrams"
                              labelid="weightInGrams"
                              defaultlabelid="Grams *"
                              Value={values.eics.weightInGrams}
                              inputProps={{ maxLength: 5 }}
                              handlechange={(text: string) => {
                                setFieldValue(
                                  'eics.weightInGrams',
                                  text.replace(/\D/g, ''),
                                );
                              }}
                              sxProps={{
                                backgroundColor: 'white',
                              }}
                              placeholderOnfloatBackgroundColor="var(--thom-mono-gray3-bg)"
                            />
                          </Box>
                        </Box>
                      )}
                    </Box>
                    <Box className="align__items__center mt-md">
                      <Box className="flex__">
                        <MediumTypography
                          labelid="IntakeCurrentHealth.childDiagnosedAtBirth"
                          defaultlabel="Was child diagnosed at birth with Intrauterine Growth Restriction(IUGR) or Small for the Gestational Age (SGA) ?"
                        />
                        <MediumTypography
                          labelid="Intake.Eics"
                          defaultlabel="(EICS)"
                          sxProps={{ fontStyle: 'italic' }}
                          className="ml-xs"
                        />
                      </Box>
                      <CustomRadioButton
                        data={EICSRadioData}
                        value={values.eics.childDiagnosedAtBirth}
                        onSelectItem={(value) => {
                          if (value) {
                            setFieldValue('eics.childDiagnosedAtBirth', value);
                          }
                        }}
                      />
                    </Box>
                    <Box className="mt-md">
                      <Box className="flex__">
                        <MediumTypography
                          labelid="IntakePregnancy.BornInMultipleBirth"
                          defaultlabel="Was this baby born in a Multiple Birth?"
                        />
                        <MediumTypography
                          labelid="Intake.Eics"
                          defaultlabel="(EICS)"
                          sxProps={{ fontStyle: 'italic' }}
                          className="ml-xs"
                        />
                      </Box>
                      <CustomRadioButton
                        data={IntakeRadioData}
                        value={values.eics.multipleBirth}
                        onSelectItem={(value) => {
                          if (value) {
                            setFieldValue('eics.multipleBirth', value);
                          }
                        }}
                        disable
                      />
                    </Box>
                    <Box className="mt-md">
                      <MediumTypography
                        labelid="IntakePregnancy.complicationsDuringLabourAndDelivery"
                        defaultlabel="Please describe any complications during the labor and delivery"
                        className="mr-lg"
                      />
                      <Grid
                        container
                        direction={'row'}
                        columnSpacing={'40px'}
                        className="mt-md mb-lg"
                      >
                        {' '}
                        <Grid item xs={9} lg={9}>
                          <CommentTextbox
                            Value={values.delivery.laborComplicationDesc}
                            placeholder="Comments"
                            name="prenatalCareStart"
                            maxLength={2000}
                            handlechange={(e) => {
                              setFieldValue(
                                'delivery.laborComplicationDesc',
                                e,
                              );
                            }}
                            placeholderBackgroundColor="var(--thom-mono-gray3-bg)"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                    <Box className="mt-md">
                      <MediumTypography
                        className="mr-md"
                        labelid="IntakePregnancy.medicationsAnesthesiaDuringLabourAndDelivery"
                        defaultlabel="Did the gestational parent receive any medications or anesthesia during the labor and delivery?"
                      />
                      <CustomRadioButton
                        data={IntakeRadioData}
                        value={values.delivery.gestationalParentMedication}
                        onSelectItem={(value) => {
                          if (value) {
                            setFieldValue(
                              'delivery.gestationalParentMedication',
                              value,
                            );
                            setFieldValue(
                              'delivery.gestationalParentMedicationDesc',
                              '',
                            );
                          }
                        }}
                      />
                      {values.delivery.gestationalParentMedication ===
                        'Yes' && (
                        <Grid
                          container
                          direction={'row'}
                          columnSpacing={'40px'}
                          className="mt-md"
                        >
                          <Grid item xs={9} lg={9}>
                            <CommentTextbox
                              Value={
                                values.delivery.gestationalParentMedicationDesc
                              }
                              placeholder="Comments"
                              name="medicationsAnesthesia"
                              maxLength={2000}
                              handlechange={(e) => {
                                setFieldValue(
                                  'delivery.gestationalParentMedicationDesc',
                                  e,
                                );
                              }}
                              placeholderBackgroundColor="var(--thom-mono-gray3-bg)"
                            />
                          </Grid>
                        </Grid>
                      )}
                    </Box>
                    <Box className="mt-md">
                      <Box className="flex__">
                        <MediumTypography
                          labelid="IntakePregnancy.specialMedicalCareAndNicu"
                          defaultlabel="Did this child receive any special medical care after the birth, such as medications, or being placed in the special care nursery or Neonatal Intensive Care Unit (NICU)?"
                        />
                      </Box>
                      <CustomRadioButton
                        data={EICSRadioData}
                        value={values.eics.specialCareAfterBirth}
                        onSelectItem={(value) => {
                          if (value) {
                            setFieldValue('eics.specialCareAfterBirth', value);
                            setFieldValue('eics.specialCareAfterBirthDesc', '');
                          }
                        }}
                      />
                      {values.eics.specialCareAfterBirth === 'Yes' && (
                        <Grid
                          container
                          direction={'row'}
                          columnSpacing={'40px'}
                          className="mt-md"
                        >
                          <Grid item xs={9} lg={9}>
                            <CommentTextbox
                              Value={values.eics.specialCareAfterBirthDesc}
                              placeholder="Comments"
                              name="medicationsAnesthesia"
                              maxLength={2000}
                              handlechange={(e) => {
                                setFieldValue(
                                  'eics.specialCareAfterBirthDesc',
                                  e,
                                );
                              }}
                              placeholderBackgroundColor="var(--thom-mono-gray3-bg)"
                            />
                          </Grid>
                        </Grid>
                      )}
                    </Box>
                    <Box className="mt-md">
                      <Box className="flex__">
                        <MediumTypography
                          labelid="IntakePregnancy.childInNICU"
                          defaultlabel="Was this child in NICU ?"
                        />
                        <MediumTypography
                          labelid="Intake.Eics"
                          defaultlabel="(EICS)"
                          sxProps={{ fontStyle: 'italic' }}
                          className="ml-xs"
                        />
                      </Box>
                      <CustomRadioButton
                        data={EICSRadioData}
                        value={values.eics.childInNICU}
                        onSelectItem={(value) => {
                          if (value) {
                            if (
                              values.eics.daysInNICU &&
                              values.eics.daysInNICU !== ''
                            ) {
                              setFieldValue(
                                'daysInNICU',
                                values.eics.daysInNICU,
                              );
                            } else {
                              setFieldValue('daysInNICU', '');
                            }
                            if (value !== 'Yes') {
                              setFieldValue('daysInNICUCheck', false);
                            }
                            setFieldValue('eics.childInNICU', value);
                          }
                        }}
                      />
                      {values.eics.childInNICU === 'Yes' && (
                        <Grid
                          container
                          direction={'row'}
                          columnSpacing={'20px'}
                          className="mt-md mb-lg"
                        >
                          <Grid item xs={4.5} lg={4.5}>
                            <Textinput
                              labelid="IntakePregnancy.daysInNICU"
                              defaultlabelid="Days in NICU"
                              Value={values.eics.daysInNICU}
                              inputProps={{ maxLength: 3 }}
                              handlechange={(text: string) => {
                                setFieldValue(
                                  'eics.daysInNICU',
                                  text.replace(/\D/g, ''),
                                );
                              }}
                              sxProps={{
                                backgroundColor: 'white',
                              }}
                              placeholderOnfloatBackgroundColor="var(--thom-mono-gray3-bg)"
                            />
                          </Grid>
                          <Grid
                            item
                            xs={4.5}
                            lg={4.5}
                            sx={{ display: 'flex', alignItems: 'center' }}
                          >
                            <CheckBoxComponent
                              CheckHandleChange={(e) => {
                                if (e.target.checked) {
                                  setFieldValue('eics.daysInNICU', 'Unknown');
                                  setDaysInNICUCheck(true);
                                } else {
                                  setDaysInNICUCheck(false);
                                  setFieldValue('eics.daysInNICU', '');
                                }
                              }}
                              ischecked={daysInNICUCheck}
                              labelid="Intake.RadioLabelC"
                              defaultlabelid="Unknown"
                            />
                          </Grid>
                        </Grid>
                      )}
                    </Box>
                    <Box className="mt-md">
                      <Box className="flex__">
                        <MediumTypography
                          labelid="IntakePregnancy.DiagnosisOfSubstanceExposedNewborn"
                          defaultlabel="Diagnosis of Substance Exposed Newborn (SEN) ?"
                        />
                        <MediumTypography
                          labelid="Intake.Eics"
                          defaultlabel="(EICS)"
                          sxProps={{ fontStyle: 'italic' }}
                          className="ml-xs"
                        />
                      </Box>
                      <CustomRadioButton
                        data={EICSRadioData}
                        value={values.eics.diagnosisOfSubstanceExposedNewborn}
                        onSelectItem={(value) => {
                          if (value) {
                            setFieldValue(
                              'eics.diagnosisOfSubstanceExposedNewborn',
                              value,
                            );
                          }
                        }}
                      />
                    </Box>
                    <Box className="mt-md">
                      <Box className="flex__">
                        <MediumTypography
                          labelid="IntakeFamilyHistory.siblingWithSEN"
                          defaultlabel="Sibling with a diagnosis of Substance Exposed Newborn (SEN) ?"
                        />
                        <MediumTypography
                          labelid="Intake.Eics"
                          defaultlabel="(EICS)"
                          sxProps={{ fontStyle: 'italic' }}
                          className="ml-xs"
                        />
                      </Box>
                      <CustomRadioButton
                        data={EICSRadioData}
                        value={values.eics.siblingWithSEN}
                        onSelectItem={(value) => {
                          if (value) {
                            setFieldValue('eics.siblingWithSEN', value);
                          }
                        }}
                      />
                    </Box>
                    <Box className="mt-md">
                      <MediumTypography
                        className="mr-md"
                        labelid="IntakePregnancy.specialMedicalEquipment"
                        defaultlabel="Did the baby leave the hospital with any special medical equipment?"
                      />
                      <CustomRadioButton
                        data={IntakeRadioData}
                        value={values.tests.specialMedicalEquipment}
                        onSelectItem={(value) => {
                          if (value) {
                            setFieldValue(
                              'tests.specialMedicalEquipment',
                              value,
                            );
                            setFieldValue(
                              'tests.specialMedicalEquipmentDesc',
                              '',
                            );
                          }
                        }}
                      />
                      {values.tests.specialMedicalEquipment === 'Yes' && (
                        <Grid
                          container
                          direction={'row'}
                          columnSpacing={'40px'}
                          className="mt-md"
                        >
                          <Grid item xs={9} lg={9}>
                            <CommentTextbox
                              Value={values.tests.specialMedicalEquipmentDesc}
                              placeholder="Comments"
                              name="medicationsAnesthesia"
                              maxLength={2000}
                              handlechange={(e) => {
                                setFieldValue(
                                  'tests.specialMedicalEquipmentDesc',
                                  e,
                                );
                              }}
                              placeholderBackgroundColor="var(--thom-mono-gray3-bg)"
                            />
                          </Grid>
                        </Grid>
                      )}
                    </Box>
                    <Box className="mt-md">
                      <MediumTypography
                        labelid="IntakePregnancy.gestationalParentStayInHospital"
                        defaultlabel="How long was the gestational parent’s stay in the hospital?"
                        className="mr-lg"
                      />
                      <Grid
                        container
                        direction={'row'}
                        columnSpacing={'40px'}
                        className="mt-md mb-lg"
                      >
                        {' '}
                        <Grid item xs={9} lg={9}>
                          <CommentTextbox
                            Value={values.tests.parentStayInHospital}
                            placeholder="Comments"
                            name="prenatalCareStart"
                            maxLength={2000}
                            handlechange={(e) => {
                              setFieldValue('tests.parentStayInHospital', e);
                            }}
                            placeholderBackgroundColor="var(--thom-mono-gray3-bg)"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                    <Box className="mt-md">
                      <MediumTypography
                        labelid="IntakePregnancy.babyInHospital"
                        defaultlabel="How long was the baby in the hospital after the birth?"
                        className="mr-lg"
                      />
                      <Grid
                        container
                        direction={'row'}
                        columnSpacing={'40px'}
                        className="mt-md mb-lg"
                      >
                        {' '}
                        <Grid item xs={9} lg={9}>
                          <CommentTextbox
                            Value={values.tests.babyStayInHospital}
                            placeholder="Comments"
                            name="prenatalCareStart"
                            maxLength={2000}
                            handlechange={(e) => {
                              setFieldValue('tests.babyStayInHospital', e);
                            }}
                            placeholderBackgroundColor="var(--thom-mono-gray3-bg)"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                    <Box className="mt-md">
                      <MediumTypography
                        className="mr-md"
                        labelid="IntakePregnancy.babyHearingScreening"
                        defaultlabel="Did this child pass the newborn hearing screening?"
                      />
                      <CustomRadioButton
                        data={EICSRadioData}
                        value={values.tests.newbornHearingScreening}
                        onSelectItem={(value) => {
                          if (value) {
                            setFieldValue(
                              'tests.newbornHearingScreening',
                              value,
                            );
                          }
                        }}
                      />
                    </Box>
                    <MediumTypography
                      labelid="Intake.Pregnancy.afterTheBirth"
                      defaultlabel="After the Birth"
                      fontSize="16px"
                      fontweight={600}
                      textColor="#2A4241"
                      className="mt-md"
                    />
                    <Box component="div" className="mt-md">
                      <MediumTypography
                        className="mr-lg"
                        label="Since this child was born, has the gestational parent had times when they have felt down, worried, depressed, or hopeless?"
                      />
                      <CustomRadioButton
                        data={IntakeRadioData}
                        value={values.tests.parentHadTimes}
                        onSelectItem={(value) => {
                          if (value) {
                            if (value === 'No') {
                              setFieldValue('problemWhenChildBornDeal', '');
                              setFieldValue('problemWhenChildBornNow', '');
                              setFieldValue('tests.parentHadTimesDesc', '');
                            }
                            setFieldValue('tests.parentHadTimes', value);
                          }
                        }}
                      />
                    </Box>

                    {values.tests.parentHadTimes === 'Yes' && (
                      <Grid
                        container
                        direction={'row'}
                        columnSpacing={'40px'}
                        className="mt-md mb-lg"
                      >
                        <Grid item xs={9} lg={9}>
                          <MediumTypography
                            labelid="IntakePregnancy.parentHadTimeSubtitle"
                            defaultlabel="How did they deal with that? How are they doing now?"
                          />
                          <CommentTextbox
                            Value={values.tests.parentHadTimesDesc}
                            placeholder="Comments"
                            name="problemWhenChildBornDeal"
                            maxLength={2000}
                            handlechange={(e) => {
                              setFieldValue('tests.parentHadTimesDesc', e);
                            }}
                            placeholderBackgroundColor="var(--thom-mono-gray3-bg)"
                          />
                        </Grid>
                      </Grid>
                    )}
                    <Box className="mt-md">
                      <MediumTypography
                        labelid="IntakePregnancy.gestationalParentFeeling"
                        defaultlabel="How did the gestational parent feel physically after the birth?"
                      />
                      <Grid
                        container
                        direction={'row'}
                        columnSpacing={'40px'}
                        className="mt-md mb-lg"
                      >
                        <Grid item xs={9} lg={9}>
                          <CommentTextbox
                            Value={values.tests.parentPhysicalFeel}
                            placeholder="Comments"
                            name="problemWhenChildBornDeal"
                            maxLength={2000}
                            handlechange={(e) => {
                              setFieldValue('tests.parentPhysicalFeel', e);
                            }}
                            placeholderBackgroundColor="var(--thom-mono-gray3-bg)"
                          />
                        </Grid>
                      </Grid>
                    </Box>

                    <Box className="mt-md">
                      <MediumTypography
                        labelid="Intake.additionalCommentsTitle"
                        defaultlabel="Additional Comments"
                        fontSize="16px"
                        fontweight={600}
                        textColor="#2A4241"
                      />
                    </Box>
                    <Grid
                      container
                      direction={'row'}
                      columnSpacing={'40px'}
                      className="mt-md mb-lg"
                    >
                      <Grid item xs={9} lg={9}>
                        <CommentTextbox
                          Value={values.comment}
                          placeholder="Comments"
                          name="comment"
                          maxLength={3000}
                          handlechange={(e) => {
                            setFieldValue('comment', e);
                          }}
                          placeholderBackgroundColor="var(--thom-mono-gray3-bg)"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                  <ModalPopup
                    description="formUnsavedChangesMessage"
                    open={openModal}
                    onCancel={() => {
                      setOpenModal(false);
                    }}
                    onOk={() => {
                      formikRef.current?.resetForm();

                      setIntakePregnancyInfo({
                        ...intakePregnancyInfo,
                      });
                      setOpenModal(false);
                    }}
                    labelId1="Clientpage.Nobtn"
                    negativeActionLabel="No"
                    labelId2="Clientpage.Yesbtn"
                    positiveActionLabel="Yes"
                  />
                </Box>
              );
            }}
          </Formik>
        </Card>
        <Box
          className="mt-md mb-xlg"
          display={'flex'}
          justifyContent={'flex-end'}
        >
          <ButtonComponent
            className="btn-primary btn-cancel alrt_Width_btn mr-md"
            variantType="contained"
            labelId={'cancelText'}
            defaultLabelId={'Cancel'}
            sxProps={{
              display: hasEditPermission ? 'auto' : 'none',
            }}
            onClick={() =>
              handleCancellationFlow()
                ? setOpenModal(true)
                : setOpenModal(false)
            }
          />

          <ButtonComponent
            variantType="contained"
            type="submit"
            sxProps={{
              display: hasEditPermission ? 'auto' : 'none',
            }}
            className="btn-primary btn-submit alrt_Width_btn"
            labelId={'Intake.submit'}
            defaultLabelId={'Save & Next'}
            onClick={() => {
              formikRef.current?.handleSubmit();
            }}
          />
        </Box>
      </Box>
    </>
  );
};

export default IntakePregnancyInfo;
